<template>
  <div class="flex flex-col gap-4">
    <BaseCard class="p-4 md:p-6">
      <LoadingWrapper :loading="store.loading" :error="store.loadingFailed">
        <MessageComponent
          v-if="!groupedNotifications.length"
          in-card
          color="info"
        >
          Er zijn geen notificaties.
        </MessageComponent>
        <template v-else>
          <div class="flex flex-col gap-8">
            <div v-for="group in groupedNotifications" :key="group.dateKey">
              <TextSubtitle class="mb-4">{{ group.dateLabel }}</TextSubtitle>
              <div class="divide-gray-75 flex flex-col divide-y">
                <div
                  v-for="notification in group.notifications"
                  :key="notification.id"
                  class="py-4 first:pt-0 last:pb-0"
                >
                  <NotificationItemComponent :notification="notification" />
                </div>
              </div>
            </div>
          </div>
          <div v-if="canLoadMore" class="mt-4 flex justify-center">
            <BaseButton color="primary" small outline @click="loadMore">
              Nog {{ remainingCount }} notificaties
            </BaseButton>
          </div>
        </template>
      </LoadingWrapper>
    </BaseCard>
  </div>
</template>

<script setup>
import { computed } from "vue"
import { format } from "date-fns"
import { nl } from "date-fns/locale"
import useNotificationStore from "@/stores/notificationStore.js"
import useNotificationsListStore from "@/stores/repower/notificationsListStore.js"
import NotificationItemComponent from "@/components/notifications/NotificationItemComponent.vue"
import MessageComponent from "@/components/information/MessageComponent.vue"
import {
  BaseButton,
  BaseCard,
  LoadingWrapper,
  TextSubtitle,
} from "@repowerednl/ui-component-library"

const notificationStore = useNotificationStore()
const store = useNotificationsListStore()

// ---- Computed ----

const sortedNotifications = computed(() =>
  [...store.notifications].sort(
    (a, b) => new Date(b.timestamp) - new Date(a.timestamp),
  ),
)

const canLoadMore = computed(() => store.nextUrl !== null)

const remainingCount = computed(() => store.count - store.notifications.length)

const groupedNotifications = computed(() => {
  const groups = []
  const seenDates = new Map()

  for (const notification of sortedNotifications.value) {
    const date = new Date(notification.timestamp)
    if (!isNaN(date)) {
      const dateKey = format(date, "yyyy-MM-dd")

      if (!seenDates.has(dateKey)) {
        const group = {
          dateKey,
          dateLabel: format(date, "d MMMM yyyy", { locale: nl }),
          notifications: [],
        }
        groups.push(group)
        seenDates.set(dateKey, group)
      }

      seenDates.get(dateKey).notifications.push(notification)
    }
  }

  return groups
})

// ---- Pagination ----

async function loadMore() {
  try {
    await store.loadMore()
  } catch (error) {
    notificationStore.pushError(
      "Laden mislukt",
      `De volgende pagina kon niet worden opgehaald (code: ${error?.code ?? "onbekend"})`,
      "load-more-notifications-error",
    )
  }
}

store.markAllAsSeen()
</script>
